/**
 * UI state store (Zustand).
 *
 * Holds ephemeral UI state: selected node, layout preference, panel visibility,
 * and subtree-filter mode for large graphs.
 * Server state (sessions, graph data) lives in TanStack Query.
 */

import { create } from "zustand";

/** UI-facing layout names. Map to the actual algorithms inside the graph
 * component:
 *   "hierarchical" → dagre (fast, default for ≤500 nodes)
 *   "force"        → elkjs's layered algorithm (used for dense graphs) */
export type GraphLayout = "hierarchical" | "force";

interface UIState {
  selectedNodeId: string | null;
  graphLayout: GraphLayout;
  /** Whether the user has explicitly chosen a layout (skips auto-selection). */
  graphLayoutExplicit: boolean;
  isPanelOpen: boolean;
  /** When true, only nodes within hop-radius of the focus node are rendered. */
  subtreeFilterEnabled: boolean;

  setSelectedNodeId: (id: string | null) => void;
  setGraphLayout: (layout: GraphLayout, explicit?: boolean) => void;
  setPanelOpen: (open: boolean) => void;
  clearSelection: () => void;
  setSubtreeFilterEnabled: (enabled: boolean) => void;
}

export const useUIStore = create<UIState>((set) => ({
  selectedNodeId: null,
  graphLayout: "hierarchical",
  graphLayoutExplicit: false,
  isPanelOpen: false,
  subtreeFilterEnabled: false,

  setSelectedNodeId: (id) =>
    set({ selectedNodeId: id, isPanelOpen: id !== null }),
  setGraphLayout: (layout, explicit = true) =>
    set({ graphLayout: layout, graphLayoutExplicit: explicit }),
  setPanelOpen: (open) => set({ isPanelOpen: open }),
  clearSelection: () =>
    set({ selectedNodeId: null, isPanelOpen: false }),
  setSubtreeFilterEnabled: (enabled) =>
    set({ subtreeFilterEnabled: enabled }),
}));
